/**
 * ImageProcessor — turns a photo into a smaller photo. Nothing else.
 *
 * This is the browser's answer to sharp. createImageBitmap decodes on the GPU
 * off the main thread, and `imageOrientation: 'from-image'` makes it honour the
 * camera's EXIF so previews come out the right way up without us rotating pixels.
 */

import { fitWithin } from '../domain/preview-size';

export interface ResizedImage {
  bytes: Uint8Array;
  width: number;
  height: number;
  /** Dimensions of the source, after EXIF orientation was applied. */
  sourceWidth: number;
  sourceHeight: number;
}

export interface ImageProcessor {
  resize(source: Blob, longEdge: number, quality: number): Promise<ResizedImage>;
}

export class CanvasImageProcessor implements ImageProcessor {
  static get isSupported(): boolean {
    return typeof createImageBitmap === 'function' && typeof OffscreenCanvas === 'function';
  }

  async resize(source: Blob, longEdge: number, quality = 0.85): Promise<ResizedImage> {
    const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' });

    try {
      const { width, height } = fitWithin(bitmap.width, bitmap.height, longEdge);
      const canvas = new OffscreenCanvas(width, height);
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error('Could not get a 2D context for resizing.');

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, width, height);

      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
      return {
        bytes: new Uint8Array(await blob.arrayBuffer()),
        width,
        height,
        sourceWidth: bitmap.width,
        sourceHeight: bitmap.height,
      };
    } finally {
      bitmap.close();
    }
  }
}
